import { useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getQuiz, type Quiz } from "@/lib/quiz-api";
import { Layout } from "@/components/layout";
import { Eye, Loader2 } from "lucide-react";
import EditQuiz from "./edit-quiz";
import QuizResults from "./quiz-results";

export default function QuizInternal() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const internalId = params?.internalId;

  // Early return if no internal ID is provided
  if (!internalId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-6 text-center">
            <p className="text-muted-foreground mb-4">Invalid quiz URL</p>
            <Button onClick={() => setLocation("/quizzes")}>
              Back to Quizzes
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  const { data: quiz, isLoading } = useQuery<Quiz>({
    queryKey: [`/api/quiz/${internalId}`],
    queryFn: () => getQuiz(internalId),
    enabled: true,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!quiz) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-6 text-center">
            <p className="text-muted-foreground mb-4">Quiz not found</p>
            <Button onClick={() => setLocation("/quizzes")}>
              Back to Quizzes
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="mb-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif text-gray-800 mb-2">{quiz.title}</h1>
              <p className="text-gray-500">
                Share Code: <code className="bg-gray-100 px-2 py-1 rounded">{quiz.shareCode}</code>
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setLocation(`/quiz/t/${quiz.shareCode}`)}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Quiz
            </Button>
          </div>
        </div>

        <Tabs defaultValue="edit" className="space-y-4">
          <TabsList className="w-full">
            <TabsTrigger value="edit" className="flex-1">Edit Quiz</TabsTrigger>
            <TabsTrigger value="results" className="flex-1">Results</TabsTrigger>
          </TabsList>

          <TabsContent value="edit">
            <EditQuiz params={{ internalId: quiz.internalId }} />
          </TabsContent>

          <TabsContent value="results">
            <QuizResults params={{ internalId: quiz.internalId }} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}